const MARKET_TZ = {
    tw: 'Asia/Taipei',
    us: 'America/New_York',
};
/**
 * 台股休市日 fallback（寫死）。台股卡農曆（春節/清明/端午/中秋）+ 行政院每年人為調整與彈性放假，
 * 無法用簡單規則推算，只能查表。這份是「最終 fallback」——正式運作以 NAS 每日推送的 TWSE 官方
 * 行事曆（`setTwHolidayOverride`）為準；override 不存在（NAS 未上線 / Cosmos 暫時讀不到）才用這份。
 * ⚠️ 仍需每年初手動補新年度（或靠 override 自動覆蓋）。2026 已對齊 TWSE OpenAPI holidaySchedule
 * （含春節 2/12–2/13「市場無交易」、教師節 9/28、光復節 10/26 補假——這些是寫死容易漏的）。
 */
const TW_HOLIDAYS_FALLBACK = new Set([
    '2025-01-01', '2025-01-27', '2025-01-28', '2025-01-29', '2025-01-30', '2025-01-31',
    '2025-02-28', '2025-04-03', '2025-04-04', '2025-05-01', '2025-05-30', '2025-10-06',
    '2025-10-10',
    '2026-01-01',
    '2026-02-12', '2026-02-13', '2026-02-16', '2026-02-17', '2026-02-18', '2026-02-19', '2026-02-20',
    '2026-02-27', '2026-04-03', '2026-04-06', '2026-05-01', '2026-06-19',
    '2026-09-25', '2026-09-28', '2026-10-09', '2026-10-26', '2026-12-25',
]);
// 台股 override（NAS 每日推送 TWSE 官方休市表）。掛 globalThis singleton：esbuild 把 shared
// inline 進每個 function bundle，裸 module-level state 跨 bundle 不共享（見 patterns memory）。
const TW_OVERRIDE_KEY = Symbol.for('invest-copilot.market-calendar.tw-override');
function twOverrideState() {
    const g = globalThis;
    let s = g[TW_OVERRIDE_KEY];
    if (!s) {
        s = { holidays: null };
        g[TW_OVERRIDE_KEY] = s;
    }
    return s;
}
/** 設定 / 清除台股 override 休市日（ISO yyyy-mm-dd）。傳 null 或空陣列＝清除、回退寫死 fallback。 */
export function setTwHolidayOverride(holidays) {
    twOverrideState().holidays = holidays && holidays.length ? new Set(holidays) : null;
}
/** 目前生效的台股 override 休市日（無則 null，代表用寫死 fallback）。 */
export function getTwHolidayOverride() {
    return twOverrideState().holidays;
}
function twHolidaySet() {
    return twOverrideState().holidays ?? TW_HOLIDAYS_FALLBACK;
}
// ── 美股假日：演算法計算（西曆固定 + 第 N 個週幾 + Good Friday + NYSE 觀察日規則）──
// 美股假日規則固定可算，不像台股卡農曆，所以不寫死、不需 API、可算到無限未來。
function pad2(n) { return n < 10 ? `0${n}` : `${n}`; }
function isoOf(year, month, day) { return `${year}-${pad2(month)}-${pad2(day)}`; }
function dowOf(year, month, day) {
    return new Date(Date.UTC(year, month - 1, day)).getUTCDay(); // 0=Sun..6=Sat
}
function shiftIso(year, month, day, deltaDays) {
    const d = new Date(Date.UTC(year, month - 1, day));
    d.setUTCDate(d.getUTCDate() + deltaDays);
    return d.toISOString().slice(0, 10);
}
/** 該月第 n 個 dow（1-indexed）。 */
function nthWeekday(year, month, dow, n) {
    const first = dowOf(year, month, 1);
    return isoOf(year, month, 1 + ((dow - first + 7) % 7) + (n - 1) * 7);
}
/** 該月最後一個 dow。 */
function lastWeekday(year, month, dow) {
    const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const last = dowOf(year, month, days);
    return isoOf(year, month, days - ((last - dow + 7) % 7));
}
/** 固定日期假日的 NYSE 觀察日：逢週六→前週五、逢週日→次週一（元旦逢週六不補前一年 12/31）。 */
function observedFixed(year, month, day, noSaturdayObserve = false) {
    const dow = dowOf(year, month, day);
    if (dow === 6)
        return noSaturdayObserve ? isoOf(year, month, day) : shiftIso(year, month, day, -1);
    if (dow === 0)
        return shiftIso(year, month, day, 1);
    return isoOf(year, month, day);
}
/** 復活節主日（computus, Gregorian）。 */
function easterSunday(year) {
    const a = year % 19, b = Math.floor(year / 100), c = year % 100;
    const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return { month, day };
}
function goodFriday(year) {
    const e = easterSunday(year);
    return shiftIso(year, e.month, e.day, -2);
}
const usHolidayCache = new Map();
function usHolidaySet(year) {
    const cached = usHolidayCache.get(year);
    if (cached)
        return cached;
    const s = new Set([
        observedFixed(year, 1, 1, true), // New Year's Day（逢週六不補）
        nthWeekday(year, 1, 1, 3), // Martin Luther King Jr. Day：1 月第 3 個週一
        nthWeekday(year, 2, 1, 3), // Washington's Birthday：2 月第 3 個週一
        goodFriday(year), // Good Friday
        lastWeekday(year, 5, 1), // Memorial Day：5 月最後週一
        observedFixed(year, 6, 19), // Juneteenth（2021 起）
        observedFixed(year, 7, 4), // Independence Day
        nthWeekday(year, 9, 1, 1), // Labor Day：9 月第 1 個週一
        nthWeekday(year, 11, 4, 4), // Thanksgiving：11 月第 4 個週四
        observedFixed(year, 12, 25), // Christmas
    ]);
    usHolidayCache.set(year, s);
    return s;
}
const usHalfDayCache = new Map();
function usHalfDaySet(year) {
    const cached = usHalfDayCache.get(year);
    if (cached)
        return cached;
    const s = new Set();
    // 感恩節隔天（週五）：固定半天（13:00 收盤）
    const thanks = nthWeekday(year, 11, 4, 4);
    s.add(shiftIso(year, Number(thanks.slice(5, 7)), Number(thanks.slice(8, 10)), 1));
    // July 3：iff July 4 落週二~週五（前一天為平日交易日、非 observed 全休）
    const jul4 = dowOf(year, 7, 4);
    if (jul4 >= 2 && jul4 <= 5)
        s.add(isoOf(year, 7, 3));
    // Dec 24：iff Dec 25 落週二~週五
    const dec25 = dowOf(year, 12, 25);
    if (dec25 >= 2 && dec25 <= 5)
        s.add(isoOf(year, 12, 24));
    usHalfDayCache.set(year, s);
    return s;
}
/**
 * 解析 TWSE OpenAPI holidaySchedule（NAS 推送的原始 rows）→ ISO 休市日清單。
 * 來源格式：`[{ Date:"1150101"(民國年7碼), Name, Weekday, Description }, ...]`。
 * 規則：① 民國年轉西元；② 排除「開始交易 / 最後交易」這種其實有交易的備註列；其餘為休市日。
 * 放 shared 確保 NAS / 後端解析一致（NAS 只搬原始 rows、語意解析統一在這）。
 */
export function parseTwseHolidaySchedule(rows) {
    if (!Array.isArray(rows))
        return [];
    const out = new Set();
    for (const row of rows) {
        if (!row || typeof row !== 'object')
            continue;
        const r = row;
        const dateRaw = String(r.Date ?? '').trim();
        const name = String(r.Name ?? '');
        if (!/^\d{7}$/.test(dateRaw))
            continue; // 民國年 7 碼 yyymmdd
        if (/開始交易|最後交易/.test(name))
            continue; // 這些其實有交易，不是休市
        const yyyy = Number(dateRaw.slice(0, 3)) + 1911;
        const mm = dateRaw.slice(3, 5);
        const dd = dateRaw.slice(5, 7);
        if (yyyy < 2000 || yyyy > 2100)
            continue;
        if (Number(mm) < 1 || Number(mm) > 12 || Number(dd) < 1 || Number(dd) > 31)
            continue;
        out.add(`${yyyy}-${mm}-${dd}`);
    }
    return Array.from(out).sort();
}
function formatDateKey(date, timeZone) {
    // `new Date('YYYY-MM-DD')` is UTC midnight. Treat that as an explicit date literal
    // so node import tests do not shift US dates to the previous local day.
    if (date.getUTCHours() === 0 &&
        date.getUTCMinutes() === 0 &&
        date.getUTCSeconds() === 0 &&
        date.getUTCMilliseconds() === 0) {
        return date.toISOString().slice(0, 10);
    }
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);
    const year = parts.find((p) => p.type === 'year')?.value ?? '1970';
    const month = parts.find((p) => p.type === 'month')?.value ?? '01';
    const day = parts.find((p) => p.type === 'day')?.value ?? '01';
    return `${year}-${month}-${day}`;
}
function addDays(dateKey, days) {
    const d = new Date(`${dateKey}T00:00:00.000Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
}
function utcDayOfWeek(dateKey) {
    return new Date(`${dateKey}T00:00:00.000Z`).getUTCDay();
}
function isWeekendDate(dateKey) {
    const day = utcDayOfWeek(dateKey);
    return day === 0 || day === 6;
}
function isHolidayDate(market, dateKey) {
    if (market === 'tw')
        return twHolidaySet().has(dateKey);
    return usHolidaySet(Number(dateKey.slice(0, 4))).has(dateKey);
}
function closedReason(market, dateKey) {
    if (isWeekendDate(dateKey))
        return 'weekend';
    if (isHolidayDate(market, dateKey))
        return 'holiday';
    return undefined;
}
function isSpecialClose(market, dateKey) {
    return market === 'us' && usHalfDaySet(Number(dateKey.slice(0, 4))).has(dateKey);
}
function zonedMinutes(date, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(date);
    const rawHour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
    const hour = rawHour === 24 ? 0 : rawHour;
    const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
    return hour * 60 + minute;
}
function sessionFor(market, date, halfDay) {
    const minutes = zonedMinutes(date, MARKET_TZ[market]);
    if (market === 'tw') {
        if (minutes >= 8 * 60 + 30 && minutes < 9 * 60)
            return 'pre-open';
        if (minutes >= 9 * 60 && minutes <= 13 * 60 + 30)
            return 'regular';
        if (minutes > 13 * 60 + 30)
            return 'after-hours';
        return 'closed';
    }
    const regularClose = halfDay ? 13 * 60 : 16 * 60;
    if (minutes >= 4 * 60 && minutes < 9 * 60 + 30)
        return 'pre-open';
    if (minutes >= 9 * 60 + 30 && minutes <= regularClose)
        return 'regular';
    if (minutes > regularClose)
        return 'after-hours';
    return 'closed';
}
function tradingDayAt(market, dateKey) {
    return closedReason(market, dateKey) === undefined;
}
export function getPreviousTradingDay(market, date = new Date()) {
    let cursor = addDays(formatDateKey(date, MARKET_TZ[market]), -1);
    for (let i = 0; i < 14; i++) {
        if (tradingDayAt(market, cursor))
            return cursor;
        cursor = addDays(cursor, -1);
    }
    return cursor;
}
export function getNextTradingDay(market, date = new Date()) {
    let cursor = addDays(formatDateKey(date, MARKET_TZ[market]), 1);
    for (let i = 0; i < 14; i++) {
        if (tradingDayAt(market, cursor))
            return cursor;
        cursor = addDays(cursor, 1);
    }
    return cursor;
}
export function getMarketCalendarStatus(market, date = new Date()) {
    const dateKey = formatDateKey(date, MARKET_TZ[market]);
    const reason = closedReason(market, dateKey);
    const halfDay = isSpecialClose(market, dateKey);
    const isTrading = reason === undefined;
    return {
        market,
        date: dateKey,
        isTradingDay: isTrading,
        reason: reason ?? (halfDay ? 'special-close' : 'normal'),
        session: isTrading ? sessionFor(market, date, halfDay) : 'closed',
        nextTradingDay: getNextTradingDay(market, date),
        previousTradingDay: getPreviousTradingDay(market, date),
    };
}
export function isTradingDay(market, date = new Date()) {
    return getMarketCalendarStatus(market, date).isTradingDay;
}
export function getMarketDateKey(market, date = new Date()) {
    return formatDateKey(date, MARKET_TZ[market]);
}
const MARKET_OPEN_MINUTES = {
    tw: 9 * 60,
    us: 9 * 60 + 30,
};
const MARKET_CLOSE_MINUTES = {
    tw: 13 * 60 + 30,
    us: 16 * 60,
};
const US_HALF_DAY_CLOSE_MINUTES = 13 * 60;
/** IANA timezone for a market (e.g. 'Asia/Taipei'). */
export function getMarketTimeZone(market) {
    return MARKET_TZ[market];
}
/** Regular-session open time, in minutes-into-day in the market timezone. */
export function getMarketOpenMinutes(market) {
    return MARKET_OPEN_MINUTES[market];
}
/** Regular-session close time, in minutes-into-day. `dateKey` lets US half-days return 13:00. */
export function getMarketCloseMinutes(market, dateKey) {
    if (market === 'us' && dateKey && usHalfDaySet(Number(dateKey.slice(0, 4))).has(dateKey))
        return US_HALF_DAY_CLOSE_MINUTES;
    return MARKET_CLOSE_MINUTES[market];
}
//# sourceMappingURL=market-calendar.js.map